import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  User,
  Mail,
  Phone,
  Camera,
  Lock,
  X,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  ChevronLeft,
  Loader2,
  ChevronDown,
  Check,
} from "lucide-react";
import LoadingIndicator from "../../pages/LoadingIndicator";
import AvatarEditor from "react-avatar-editor";
import defaultUserIcon from "../../Images/icon_user.webp";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// Couleurs du drapeau du Niger
const COLORS = {
  orange: "#E87E04",
  white: "#FFFFFF",
  green: "#008751",
  darkGreen: "#006B3F",
  lightOrange: "#FFE0B2",
  lightGreen: "#CCFFCC",
  gray: "#718096",
  lightGray: "#E2E8F0",
  paleGreen: "#E6FFEA",
};

// Liste des indicatifs téléphoniques populaires (repris de la page d'inscription)
const countryCodes = [
  { code: "+93", country: "Afghanistan", flag: "🇦🇫" },
  { code: "+355", country: "Albanie", flag: "🇦🇱" },
  { code: "+213", country: "Algérie", flag: "🇩🇿" },
  { code: "+376", country: "Andorre", flag: "🇦🇩" },
  { code: "+244", country: "Angola", flag: "🇦🇴" },
  { code: "+54", country: "Argentine", flag: "🇦🇷" },
  { code: "+374", country: "Arménie", flag: "🇦🇲" },
  { code: "+61", country: "Australie", flag: "🇦🇺" },
  { code: "+43", country: "Autriche", flag: "🇦🇹" },
  { code: "+994", country: "Azerbaïdjan", flag: "🇦🇿" },
  { code: "+1242", country: "Bahamas", flag: "🇧🇸" },
  { code: "+973", country: "Bahreïn", flag: "🇧🇭" },
  { code: "+880", country: "Bangladesh", flag: "🇧🇩" },
  { code: "+32", country: "Belgique", flag: "🇧🇪" },
  { code: "+229", country: "Bénin", flag: "🇧🇯" },
  { code: "+975", country: "Bhoutan", flag: "🇧🇹" },
  { code: "+591", country: "Bolivie", flag: "🇧🇴" },
  { code: "+387", country: "Bosnie-Herzégovine", flag: "🇧🇦" },
  { code: "+267", country: "Botswana", flag: "🇧🇼" },
  { code: "+55", country: "Brésil", flag: "🇧🇷" },
  { code: "+673", country: "Brunei", flag: "🇧🇳" },
  { code: "+359", country: "Bulgarie", flag: "🇧🇬" },
  { code: "+226", country: "Burkina Faso", flag: "🇧🇫" },
  { code: "+257", country: "Burundi", flag: "🇧🇮" },
  { code: "+855", country: "Cambodge", flag: "🇰🇭" },
  { code: "+237", country: "Cameroun", flag: "🇨🇲" },
  { code: "+1", country: "Canada", flag: "🇨🇦" },
  { code: "+238", country: "Cap-Vert", flag: "🇨🇻" },
  { code: "+236", country: "République centrafricaine", flag: "🇨🇫" },
  { code: "+235", country: "Tchad", flag: "🇹🇩" },
  { code: "+56", country: "Chili", flag: "🇨🇱" },
  { code: "+86", country: "Chine", flag: "🇨🇳" },
  { code: "+57", country: "Colombie", flag: "🇨🇴" },
  { code: "+242", country: "Congo", flag: "🇨🇬" },
  { code: "+243", country: "RD Congo", flag: "🇨🇩" },
  { code: "+506", country: "Costa Rica", flag: "🇨🇷" },
  { code: "+225", country: "Côte d'Ivoire", flag: "🇨🇮" },
  { code: "+385", country: "Croatie", flag: "🇭🇷" },
  { code: "+53", country: "Cuba", flag: "🇨🇺" },
  { code: "+357", country: "Chypre", flag: "🇨🇾" },
  { code: "+420", country: "République tchèque", flag: "🇨🇿" },
  { code: "+45", country: "Danemark", flag: "🇩🇰" },
  { code: "+253", country: "Djibouti", flag: "🇩🇯" },
  { code: "+1809", country: "République dominicaine", flag: "🇩🇴" },
  { code: "+593", country: "Équateur", flag: "🇪🇨" },
  { code: "+20", country: "Égypte", flag: "🇪🇬" },
  { code: "+503", country: "El Salvador", flag: "🇸🇻" },
  { code: "+240", country: "Guinée équatoriale", flag: "🇬🇶" },
  { code: "+291", country: "Érythrée", flag: "🇪🇷" },
  { code: "+372", country: "Estonie", flag: "🇪🇪" },
  { code: "+251", country: "Éthiopie", flag: "🇪🇹" },
  { code: "+679", country: "Fidji", flag: "🇫🇯" },
  { code: "+358", country: "Finlande", flag: "🇫🇮" },
  { code: "+33", country: "France", flag: "🇫🇷" },
  { code: "+241", country: "Gabon", flag: "🇬🇦" },
  { code: "+220", country: "Gambie", flag: "🇬🇲" },
  { code: "+995", country: "Géorgie", flag: "🇬🇪" },
  { code: "+49", country: "Allemagne", flag: "🇩🇪" },
  { code: "+233", country: "Ghana", flag: "🇬🇭" },
  { code: "+30", country: "Grèce", flag: "🇬🇷" },
  { code: "+299", country: "Groenland", flag: "🇬🇱" },
  { code: "+502", country: "Guatemala", flag: "🇬🇹" },
  { code: "+224", country: "Guinée", flag: "🇬🇳" },
  { code: "+245", country: "Guinée-Bissau", flag: "🇬🇼" },
  { code: "+592", country: "Guyana", flag: "🇬🇾" },
  { code: "+509", country: "Haïti", flag: "🇭🇹" },
  { code: "+504", country: "Honduras", flag: "🇭🇳" },
  { code: "+852", country: "Hong Kong", flag: "🇭🇰" },
  { code: "+36", country: "Hongrie", flag: "🇭🇺" },
  { code: "+354", country: "Islande", flag: "🇮🇸" },
  { code: "+91", country: "Inde", flag: "🇮🇳" },
  { code: "+62", country: "Indonésie", flag: "🇮🇩" },
  { code: "+98", country: "Iran", flag: "🇮🇷" },
  { code: "+964", country: "Irak", flag: "🇮🇶" },
  { code: "+353", country: "Irlande", flag: "🇮🇪" },
  { code: "+972", country: "Israël", flag: "🇮🇱" },
  { code: "+39", country: "Italie", flag: "🇮🇹" },
  { code: "+1876", country: "Jamaïque", flag: "🇯🇲" },
  { code: "+81", country: "Japon", flag: "🇯🇵" },
  { code: "+962", country: "Jordanie", flag: "🇯🇴" },
  { code: "+7", country: "Kazakhstan", flag: "🇰🇿" },
  { code: "+254", country: "Kenya", flag: "🇰🇪" },
  { code: "+965", country: "Koweït", flag: "🇰🇼" },
  { code: "+996", country: "Kirghizistan", flag: "🇰🇬" },
  { code: "+856", country: "Laos", flag: "🇱🇦" },
  { code: "+371", country: "Lettonie", flag: "🇱🇻" },
  { code: "+961", country: "Liban", flag: "🇱🇧" },
  { code: "+266", country: "Lesotho", flag: "🇱🇸" },
  { code: "+231", country: "Libéria", flag: "🇱🇷" },
  { code: "+218", country: "Libye", flag: "🇱🇾" },
  { code: "+423", country: "Liechtenstein", flag: "🇱🇮" },
  { code: "+370", country: "Lituanie", flag: "🇱🇹" },
  { code: "+352", country: "Luxembourg", flag: "🇱🇺" },
  { code: "+853", country: "Macao", flag: "🇲🇴" },
  { code: "+389", country: "Macédoine du Nord", flag: "🇲🇰" },
  { code: "+261", country: "Madagascar", flag: "🇲🇬" },
  { code: "+265", country: "Malawi", flag: "🇲🇼" },
  { code: "+60", country: "Malaisie", flag: "🇲🇾" },
  { code: "+960", country: "Maldives", flag: "🇲🇻" },
  { code: "+223", country: "Mali", flag: "🇲🇱" },
  { code: "+356", country: "Malte", flag: "🇲🇹" },
  { code: "+222", country: "Mauritanie", flag: "🇲🇷" },
  { code: "+230", country: "Maurice", flag: "🇲🇺" },
  { code: "+52", country: "Mexique", flag: "🇲🇽" },
  { code: "+373", country: "Moldavie", flag: "🇲🇩" },
  { code: "+377", country: "Monaco", flag: "🇲🇨" },
  { code: "+976", country: "Mongolie", flag: "🇲🇳" },
  { code: "+382", country: "Monténégro", flag: "🇲🇪" },
  { code: "+212", country: "Maroc", flag: "🇲🇦" },
  { code: "+258", country: "Mozambique", flag: "🇲🇿" },
  { code: "+95", country: "Myanmar", flag: "🇲🇲" },
  { code: "+264", country: "Namibie", flag: "🇳🇦" },
  { code: "+977", country: "Népal", flag: "🇳🇵" },
  { code: "+31", country: "Pays-Bas", flag: "🇳🇱" },
  { code: "+64", country: "Nouvelle-Zélande", flag: "🇳🇿" },
  { code: "+505", country: "Nicaragua", flag: "🇳🇮" },
  { code: "+227", country: "Niger", flag: "🇳🇪" },
  { code: "+234", country: "Nigeria", flag: "🇳🇬" },
  { code: "+850", country: "Corée du Nord", flag: "🇰🇵" },
  { code: "+47", country: "Norvège", flag: "🇳🇴" },
  { code: "+968", country: "Oman", flag: "🇴🇲" },
  { code: "+92", country: "Pakistan", flag: "🇵🇰" },
  { code: "+970", country: "Palestine", flag: "🇵🇸" },
  { code: "+507", country: "Panama", flag: "🇵🇦" },
  { code: "+675", country: "Papouasie-Nouvelle-Guinée", flag: "🇵🇬" },
  { code: "+595", country: "Paraguay", flag: "🇵🇾" },
  { code: "+51", country: "Pérou", flag: "🇵🇪" },
  { code: "+63", country: "Philippines", flag: "🇵🇭" },
  { code: "+48", country: "Pologne", flag: "🇵🇱" },
  { code: "+351", country: "Portugal", flag: "🇵🇹" },
  { code: "+974", country: "Qatar", flag: "🇶🇦" },
  { code: "+40", country: "Roumanie", flag: "🇷🇴" },
  { code: "+7", country: "Russie", flag: "🇷🇺" },
  { code: "+250", country: "Rwanda", flag: "🇷🇼" },
  { code: "+966", country: "Arabie saoudite", flag: "🇸🇦" },
  { code: "+221", country: "Sénégal", flag: "🇸🇳" },
  { code: "+381", country: "Serbie", flag: "🇷🇸" },
  { code: "+232", country: "Sierra Leone", flag: "🇸🇱" },
  { code: "+65", country: "Singapour", flag: "🇸🇬" },
  { code: "+421", country: "Slovaquie", flag: "🇸🇰" },
  { code: "+386", country: "Slovénie", flag: "🇸🇮" },
  { code: "+252", country: "Somalie", flag: "🇸🇴" },
  { code: "+27", country: "Afrique du Sud", flag: "🇿🇦" },
  { code: "+82", country: "Corée du Sud", flag: "🇰🇷" },
  { code: "+34", country: "Espagne", flag: "🇪🇸" },
  { code: "+94", country: "Sri Lanka", flag: "🇱🇰" },
  { code: "+249", country: "Soudan", flag: "🇸🇩" },
  { code: "+211", country: "Soudan du Sud", flag: "🇸🇸" },
  { code: "+597", country: "Suriname", flag: "🇸🇷" },
  { code: "+268", country: "Eswatini", flag: "🇸🇿" },
  { code: "+46", country: "Suède", flag: "🇸🇪" },
  { code: "+41", country: "Suisse", flag: "🇨🇭" },
  { code: "+963", country: "Syrie", flag: "🇸🇾" },
  { code: "+886", country: "Taïwan", flag: "🇹🇼" },
  { code: "+992", country: "Tadjikistan", flag: "🇹🇯" },
  { code: "+255", country: "Tanzanie", flag: "🇹🇿" },
  { code: "+66", country: "Thaïlande", flag: "🇹🇭" },
  { code: "+228", country: "Togo", flag: "🇹🇬" },
  { code: "+216", country: "Tunisie", flag: "🇹🇳" },
  { code: "+90", country: "Turquie", flag: "🇹🇷" },
  { code: "+993", country: "Turkménistan", flag: "🇹🇲" },
  { code: "+256", country: "Ouganda", flag: "🇺🇬" },
  { code: "+380", country: "Ukraine", flag: "🇺🇦" },
  { code: "+971", country: "Émirats arabes unis", flag: "🇦🇪" },
  { code: "+44", country: "Royaume-Uni", flag: "🇬🇧" },
  { code: "+1", country: "États-Unis", flag: "🇺🇸" },
  { code: "+598", country: "Uruguay", flag: "🇺🇾" },
  { code: "+998", country: "Ouzbékistan", flag: "🇺🇿" },
  { code: "+58", country: "Venezuela", flag: "🇻🇪" },
  { code: "+84", country: "Vietnam", flag: "🇻🇳" },
  { code: "+967", country: "Yémen", flag: "🇾🇪" },
  { code: "+260", country: "Zambie", flag: "🇿🇲" },
  { code: "+263", country: "Zimbabwe", flag: "🇿🇼" },
];

const ProfilePage = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState({
    nom: "",
    email: "",
    telephone: "", // Contiendra uniquement les chiffres sans indicatif
    photo: defaultUserIcon,
  });
  const [countryCode, setCountryCode] = useState("+227"); // Par défaut Niger
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingPhoto, setEditingPhoto] = useState(false);
  const [image, setImage] = useState(null);
  const [editor, setEditor] = useState(null);
  const [scale, setScale] = useState(1.2);
  const [rotate, setRotate] = useState(0);
  const [alerts, setAlerts] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [whatsapp, setWhatsapp] = useState(true);

  const regexMail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const regexPhone = /^[0-9]{8,}$/;

  // Fermer le dropdown si on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCountryDropdown && !event.target.closest(".country-dropdown")) {
        setShowCountryDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCountryDropdown]);

  const showAlert = useCallback((message, type = "info") => {
    const id = Date.now();
    setAlerts((prev) => [...prev, { id, message, type }]);
    setTimeout(
      () => setAlerts((prev) => prev.filter((alert) => alert.id !== id)),
      3000
    );
  }, []);

  const extractPhoneInfo = (fullPhoneNumber) => {
    if (!fullPhoneNumber) return { code: "+227", number: "" };

    // Parcours des indicatifs du plus long au plus court pour éviter les ambiguïtés
    const sortedCodes = countryCodes
      .map((c) => c.code)
      .sort((a, b) => b.length - a.length);

    for (const code of sortedCodes) {
      if (fullPhoneNumber.startsWith(code)) {
        return {
          code: code,
          number: fullPhoneNumber.substring(code.length),
        };
      }
    }

    // Si aucun code n'est trouvé, on suppose que c'est un numéro local
    return { code: "+227", number: fullPhoneNumber };
  };

  const fetchUserData = useCallback(
    async (userId) => {
      try {
        if (userId) {
          const user = JSON.parse(localStorage.getItem("userEcomme"));
          axios.defaults.headers.common[
            "Authorization"
          ] = `Bearer ${user.token}`;
        }

        const [userResponse] = await Promise.all([
          axios.get(`${process.env.REACT_APP_Backend_Url}/api/user/getUser`, {
            params: { id: userId },
            timeout: 10000,
          }),
        ]);

        if (!userResponse.data.user) {
          throw new Error("Données utilisateur non trouvées");
        }

        setUserData({
          nom: userResponse.data.user.name || "",
          email: userResponse.data.user.email || "",
        });

        const [profileResponse] = await Promise.all([
          axios.get(
            `${process.env.REACT_APP_Backend_Url}/api/profilesRoutes/me`,
            {
              params: { id: userId },
              timeout: 10000,
            }
          ),
        ]);
        // console.log(userResponse);
        // console.log(profileResponse);

        if (profileResponse) {
          // Extraire l'indicatif et le numéro
          const { code, number } = extractPhoneInfo(
            profileResponse.data?.data?.numero || ""
          );

          setCountryCode(code);
          setWhatsapp(profileResponse.data?.data?.whatsapp !== false); // true par défaut si non spécifié
// console.log({ddd:profileResponse.data.data.image});

          setUserData((prev) => ({
            ...prev,
            telephone: number,
            photo:
              profileResponse.data?.data?.image &&
              profileResponse.data.data.image !==
                "https://chagona.onrender.com/images/image-1688253105925-0.jpeg"
                ? profileResponse.data.data.image
                : defaultUserIcon,
          }));
        }

        setImageError(false);
        showAlert("Profil chargé avec succès", "success");
      } catch (error) {
        handleError(error);
      } finally {
        setLoading(false);
      }
    },
    [showAlert]
  );

  useEffect(() => {
    const userE = JSON.parse(localStorage.getItem("userEcomme"));
    if (!userE) {
      showAlert("Veuillez vous connecter", "error");
      setLoading(false);
      return;
    }
    fetchUserData(userE.id);
  }, [fetchUserData, showAlert]);

  const handleError = (error) => {
    let errorMessage = "Erreur lors du chargement des données";
    if (error.code === "ECONNABORTED") {
      errorMessage = "Le serveur met trop de temps à répondre";
    } else if (error.response) {
      switch (error.response.status) {
        case 404:
          errorMessage = "Profil utilisateur non trouvé";
          break;
        case 401:
          errorMessage = "Session expirée, veuillez vous reconnecter";
          break;
        case 403:
          errorMessage = "Accès non autorisé";
          break;
        case 500:
          errorMessage = "Erreur serveur, veuillez réessayer plus tard";
          break;
        default:
          errorMessage = error.response.data?.message || errorMessage;
      }
    } else if (error.request) {
      errorMessage = "Impossible de contacter le serveur";
    }

    showAlert(errorMessage, "error");
    console.error(error);
  };

  const handleImageError = () => {
    console.log("Image failed to load");
    setImageError(true);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handlePhotoSelect(file);
  };

  const handlePhotoSelect = (file) => {
    const maxSize = 4 * 1024 * 1024;
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];

    if (!allowedTypes.includes(file.type)) {
      showAlert(
        "Format non supporté (JPEG, PNG, GIF, WebP uniquement)",
        "warning"
      );
      return;
    }

    if (file.size > maxSize) {
      showAlert("Image trop volumineuse (max 4MB)", "warning");
      return;
    }

    setImage(file);
    setEditingPhoto(true);
    setScale(1.2);
    setRotate(0);
  };

  const handlePhotoChange = (e) => {
    if (e.target.files?.[0]) handlePhotoSelect(e.target.files[0]);
  };

  const handleSavePhoto = async () => {
    if (!editor) {
      showAlert("Erreur d'édition", "error");
      return;
    }

    const userE = JSON.parse(localStorage.getItem("userEcomme"));
    if (!userE) {
      showAlert("Session expirée", "error");
      return;
    }

    setIsUploadingPhoto(true);
    try {
      const canvas = editor.getImageScaledToCanvas();
      const blob = await new Promise((resolve) => canvas.toBlob(resolve));
      const formData = new FormData();
      formData.append("image", blob);
      formData.append("name", userData.nom);
      formData.append("email", userData.email);

      // Envoi du numéro complet avec indicatif
      const fullPhoneNumber = `${countryCode}${userData.telephone}`;
      formData.append("phone", fullPhoneNumber);
      formData.append("whatsapp", whatsapp);
      formData.append("id", userE.id);

      await axios.post(
        `${process.env.REACT_APP_Backend_Url}/api/profilesRoutes/create`,
        formData,
        {
          timeout: 15000,
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      showAlert("Photo mise à jour", "success");
      setEditingPhoto(false);
      await fetchUserData(userE.id);
    } catch (error) {
      handleError(error);
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const userE = JSON.parse(localStorage.getItem("userEcomme"));
    if (!userE) {
      showAlert("Session expirée", "error");
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("name", userData.nom);
      formData.append("email", userData.email);

      // Concaténer l'indicatif et le numéro
      const fullPhoneNumber = `${countryCode}${userData.telephone}`;
      formData.append("phone", fullPhoneNumber);
      formData.append("whatsapp", whatsapp);
      formData.append("id", userE.id);

      await axios.post(
        `${process.env.REACT_APP_Backend_Url}/api/profilesRoutes/create`,
        formData,
        {
          timeout: 10000,
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      showAlert("Profil mis à jour", "success");
      await fetchUserData(userE.id);
    } catch (error) {
      handleError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateForm = () => {
    const errors = [];
    if (userData.nom.trim().length < 3)
      errors.push("Nom trop court (min 3 caractères)");
    if (!regexMail.test(userData.email)) errors.push("Email invalide");
    if (!regexPhone.test(userData.telephone))
      errors.push("Numéro invalide (min 8 chiffres)");

    if (errors.length) {
      errors.forEach((error) => showAlert(error, "warning"));
      return false;
    }
    return true;
  };

  // Custom Alert Component
  const CustomAlert = ({ message, type, onClose }) => {
    const bgColor =
      type === "success"
        ? "bg-green-100 border-green-500 text-green-700"
        : type === "error"
        ? "bg-red-100 border-red-500 text-red-700"
        : type === "warning"
        ? "bg-yellow-100 border-yellow-500 text-yellow-700"
        : "bg-blue-100 border-blue-500 text-blue-700";

    return (
      <div
        className={`py-2 px-4 rounded-lg border shadow-md ${bgColor} flex items-center justify-between animate-fadeIn`}
      >
        <AlertDescription>{message}</AlertDescription>
        <button
          onClick={onClose}
          className="ml-4 text-gray-500 hover:text-gray-700"
        >
          <X size={16} />
        </button>
      </div>
    );
  };

  return (
    <LoadingIndicator loading={loading}>
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white py-8 px-4">
        {/* Back button for mobile */}
        <button
          onClick={() => navigate(-1)}
          className="md:hidden flex items-center text-green-600 mb-4 pl-2"
        >
          <ChevronLeft size={20} />
          <span>Retour</span>
        </button>

        {/* Alerts */}
        <div className="fixed top-4 right-4 z-50 space-y-2 w-full max-w-sm">
          {alerts.map((alert) => (
            <CustomAlert
              key={alert.id}
              message={alert.message}
              type={alert.type}
              onClose={() =>
                setAlerts((prev) => prev.filter((a) => a.id !== alert.id))
              }
            />
          ))}
        </div>

        <Card className="max-w-xl mx-auto shadow-lg border-t-4 border-t-green-600 overflow-hidden">
          <CardHeader className="text-center relative z-10">
            <CardTitle className="text-2xl font-bold text-green-700 flex items-center justify-center gap-2">
              <User className="h-6 w-6" />
              Mon Profil
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-6 relative z-10">
            {/* Photo Section */}
            <div className="flex flex-col items-center">
              <div
                className={`relative group cursor-pointer transition-all duration-300 ${
                  isDragging ? "scale-105" : ""
                }`}
                onDragEnter={handleDragEnter}
                onDragOver={(e) => e.preventDefault()}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <div className="w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden border-4 border-green-500 shadow-md bg-white">
                  
                  
                  <img
                    src={imageError || !userData?.photo ? defaultUserIcon : userData?.photo}
                    alt="Profil"
                    className="w-full h-full object-cover"
                    onError={handleImageError}
                    style={{ zIndex: "1000" }}
                  />
                </div>

                <input
                  type="file"
                  onChange={handlePhotoChange}
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                  id="photo-upload"
                />
                <label
                  htmlFor="photo-upload"
                  className="absolute bottom-0 right-0 bg-orange-500 text-white p-2 rounded-full cursor-pointer hover:bg-orange-600 shadow-lg transform transition-transform duration-300 hover:scale-110"
                >
                  <Camera size={18} />
                </label>
              </div>
              <p className="text-sm text-gray-500 mt-3">
                Glissez une photo ou cliquez pour modifier
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-4">
                <div className="relative">
                  <div
                    className={`absolute left-3 top-1/2 -translate-y-1/2 transition-colors duration-300 ${
                      focusedField === "nom"
                        ? "text-green-500"
                        : "text-gray-400"
                    }`}
                  >
                    <User size={18} />
                  </div>
                  <div style={{ marginLeft: 35 }}>
                    <Input
                      type="text"
                      name="nom"
                      value={userData.nom}
                      onChange={(e) =>
                        setUserData((prev) => ({
                          ...prev,
                          nom: e.target.value,
                        }))
                      }
                      onFocus={() => setFocusedField("nom")}
                      onBlur={() => setFocusedField(null)}
                      placeholder="Nom complet"
                      className="pl-10 h-12 bg-white shadow-sm border-0 ring-1 ring-gray-200 focus:ring-2 focus:ring-green-500 rounded-lg transition-all duration-300"
                    />
                  </div>
                  <div
                    className={`absolute bottom-0 left-0 w-full h-0.5 bg-green-500 transform scale-x-0 transition-transform duration-300 ${
                      focusedField === "nom" ? "scale-x-100" : ""
                    }`}
                  ></div>
                </div>

                <div className="relative">
                  <div
                    className={`absolute left-3 top-1/2 -translate-y-1/2 transition-colors duration-300 ${
                      focusedField === "email"
                        ? "text-green-500"
                        : "text-gray-400"
                    }`}
                  >
                    <Mail size={18} />
                  </div>
                  <div style={{ marginLeft: 35 }}>
                    <Input
                      type="email"
                      name="email"
                      value={userData.email}
                      onChange={(e) =>
                        setUserData((prev) => ({
                          ...prev,
                          email: e.target.value,
                        }))
                      }
                      onFocus={() => setFocusedField("email")}
                      onBlur={() => setFocusedField(null)}
                      placeholder="Email"
                      className="pl-10 h-12 bg-white shadow-sm border-0 ring-1 ring-gray-200 focus:ring-2 focus:ring-green-500 rounded-lg transition-all duration-300"
                    />
                  </div>
                  <div
                    className={`absolute bottom-0 left-0 w-full h-0.5 bg-green-500 transform scale-x-0 transition-transform duration-300 ${
                      focusedField === "email" ? "scale-x-100" : ""
                    }`}
                  ></div>
                </div>

                {/* Champ téléphone avec indicatif */}
                <div className="relative">
                  <div className="flex">
                    {/* Indicatif pays */}
                    <div className="relative country-dropdown">
                      <button
                        type="button"
                        className="inline-flex items-center px-3 py-3 border border-r-0 border-gray-300 rounded-l-md bg-gray-50 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 transition-all duration-300"
                        onClick={() =>
                          setShowCountryDropdown(!showCountryDropdown)
                        }
                      >
                        <span>
                          {
                            countryCodes.find((c) => c.code === countryCode)
                              ?.flag
                          }{" "}
                          {countryCode}
                        </span>
                        <ChevronDown className="ml-1 h-4 w-4" />
                      </button>

                      {showCountryDropdown && (
                        <div className="absolute z-10 mt-1 w-56 bg-white shadow-lg max-h-60 rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto">
                          {countryCodes.map((country) => (
                            <div
                              key={country.code}
                              className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                              onClick={() => {
                                setCountryCode(country.code);
                                setShowCountryDropdown(false);
                              }}
                            >
                              <span className="mr-2">{country.flag}</span>
                              <span>{country.country}</span>
                              <span className="ml-auto">{country.code}</span>
                              {country.code === countryCode && (
                                <Check
                                  size={16}
                                  className="ml-2 text-green-500"
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Champ numéro */}
                    <div className="relative flex-1">
                      <div
                        className={`absolute left-3 top-1/2 -translate-y-1/2 transition-colors duration-300 ${
                          focusedField === "telephone"
                            ? "text-green-500"
                            : "text-gray-400"
                        }`}
                      >
                        <Phone size={18} />
                      </div>
                      <Input
                        type="tel"
                        name="telephone"
                        value={userData.telephone}
                        onChange={(e) => {
                          // Ne permet que les chiffres
                          const value = e.target.value.replace(/\D/g, "");
                          setUserData((prev) => ({
                            ...prev,
                            telephone: value,
                          }));
                        }}
                        onFocus={() => setFocusedField("telephone")}
                        onBlur={() => setFocusedField(null)}
                        placeholder="Numéro sans indicatif"
                        className="pl-10 h-12 bg-white shadow-sm border-0 ring-1 ring-gray-200 focus:ring-2 focus:ring-green-500 rounded-r-lg transition-all duration-300"
                      />
                    </div>
                  </div>

                  {/* Option WhatsApp */}
                  <div className="mt-1 flex items-center">
                    <input
                      id="whatsapp"
                      name="whatsapp"
                      type="checkbox"
                      checked={whatsapp}
                      onChange={(e) => setWhatsapp(e.target.checked)}
                      className="h-4 w-4 text-green-500 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor="whatsapp"
                      className="ml-2 text-xs text-gray-500"
                    >
                      Ce numéro est également mon WhatsApp
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center pt-2">
                <button
                  type="button"
                  onClick={() => navigate("/Forget Password")}
                  className="inline-flex items-center text-sm text-green-600 hover:text-green-700 transition-colors"
                >
                  <Lock className="h-4 w-4 mr-1" />
                  Changer mot de passe
                </button>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 bg-green-600 hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 text-white shadow-md"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Mise à jour...
                    </>
                  ) : (
                    "Mettre à jour"
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Photo Editor Modal */}
        <Dialog open={editingPhoto} onOpenChange={setEditingPhoto}>
          <DialogContent className="sm:max-w-md bg-white rounded-xl">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold text-green-700">
                Éditer la photo
              </DialogTitle>
            </DialogHeader>

            <div className="flex justify-center my-4">
              <AvatarEditor
                ref={setEditor}
                image={image}
                width={280}
                height={280}
                border={50}
                borderRadius={140}
                color={[0, 0, 0, 0.6]}
                scale={scale}
                rotate={rotate}
                className="border rounded-lg shadow-inner"
              />
            </div>

            <div className="space-y-4">
              {/* Controls */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <ZoomOut size={20} className="text-gray-500" />
                  <Slider
                    value={[scale]}
                    min={1}
                    max={2}
                    step={0.01}
                    onValueChange={(value) => setScale(value[0])}
                    className="flex-1"
                  />
                  <ZoomIn size={20} className="text-gray-500" />
                </div>

                <div className="flex justify-center">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setRotate((r) => r + 90)}
                    className="flex items-center gap-2"
                  >
                    <RotateCcw size={16} />
                    Pivoter
                  </Button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setEditingPhoto(false)}
                  disabled={isUploadingPhoto}
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleSavePhoto}
                  disabled={isUploadingPhoto}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  {isUploadingPhoto ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sauvegarde...
                    </>
                  ) : (
                    "Sauvegarder"
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </LoadingIndicator>
  );
};

export default ProfilePage;
